import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, AutoComplete, Button, Collapse, Input, Segmented, Select, Space, Tabs, Tag, Typography, message } from 'antd';
import { CloseCircleFilled, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { getFieldCatalog, getFieldMapping, setFieldMapping } from '../../services/contractorsHubApi';

// The 6 ServiceTitan fields that are fixed constants rather than per-item
// data -- shown on their own "Constants" tab for a friendlier, non-canvas
// editing surface. Still ordinary rows on the Mapping tab/canvas too (same
// shared mapping object), so they remain draggable-onto there as well.
const CONSTANT_TARGET_PATHS = [
  'categories',
  'active',
  'taxable',
  'account',
  'costOfSaleAccount',
  'primaryVendor.vendorId',
];

const { Text } = Typography;

// A bezier curve from a source node's right edge to a target node's left
// edge, arcing horizontally -- the classic "wiring diagram" connector shape.
const connectorPath = (x1, y1, x2, y2) => {
  const dx = Math.max(40, (x2 - x1) / 2);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
};

// Best-effort type coercion so a typed-in constant value ("true", "123",
// "[1,2,3]") is stored as its real JSON type rather than always a string --
// generic (not tied to any specific field name), falls back to the raw
// string when it isn't valid JSON.
const coerceValue = (raw) => {
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return raw;
  }
};

const displayValue = (value) => {
  if (value == null) return '';
  return typeof value === 'string' ? value : JSON.stringify(value);
};

// Visual constants for the drag-and-drop canvas.
const NODE_HEIGHT = 40;
const NODE_GAP = 10;
const LINE_COLOR = '#1677ff';
const LOCKED_LINE_COLOR = '#bfbfbf';

// Shared data + handlers for both the Mapping canvas and the Constants
// panel -- one fetch, one `mapping` object, so editing a constant on
// either tab is instantly reflected on the other (same in-memory state)
// and a single Save Mapping persists everything together. `loadMapping`/
// `saveMapping` default to the tenant-wide endpoints (this screen's own
// use) but can be swapped for a per-connection (contractor + environment)
// pair -- see ContractorAccountsPanel's ConnectionConstantsSection, which
// reuses this hook and the Mapping/Constants components below unchanged.
export const useFieldMapping = ({
  loadMapping = () => getFieldMapping('service_titan'),
  saveMapping = (mapping) => setFieldMapping(mapping, 'service_titan'),
  deps = [],
} = {}) => {
  const [mapping, setMapping] = useState({});
  const [catalog, setCatalog] = useState({ target_fields: [], source_fields: [] });
  const [extraSourceFields, setExtraSourceFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [effectiveMapping, fieldCatalog] = await Promise.all([loadMapping(), getFieldCatalog('service_titan')]);
      setMapping(effectiveMapping || {});
      setCatalog(fieldCatalog || { target_fields: [], source_fields: [] });
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load field mapping');
    } finally {
      setLoading(false);
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);

  useEffect(() => {
    load();
  }, [load]);

  // Every Product field available to drag/pick: the known/confirmed
  // catalog fields, plus any source path already referenced by the
  // current mapping (so a customized mapping loaded from the server
  // always shows its own fields even if they aren't on the known list),
  // plus anything added this session via "+ Add Product Field".
  const sourceFieldList = useMemo(() => {
    const byPath = new Map();
    (catalog.source_fields || []).forEach((f) => byPath.set(f.path, f.label));
    Object.values(mapping).forEach((entry) => {
      if (entry?.mode === 'field' && entry.source && !byPath.has(entry.source)) {
        byPath.set(entry.source, entry.source);
      }
    });
    extraSourceFields.forEach((f) => {
      if (!byPath.has(f.path)) byPath.set(f.path, f.path);
    });
    return Array.from(byPath, ([path, label]) => ({ path, label }));
  }, [catalog.source_fields, mapping, extraSourceFields]);

  const sourceLabel = (path) => sourceFieldList.find((f) => f.path === path)?.label || path;
  const targetLabel = (path) => (catalog.target_fields || []).find((f) => f.path === path)?.label || path;

  const setFieldSource = (targetKey, sourceKey) => {
    setMapping((prev) => ({ ...prev, [targetKey]: { mode: 'field', source: sourceKey } }));
  };

  const handleModeChange = (targetKey, mode) => {
    setMapping((prev) => {
      const current = prev[targetKey];
      if (mode === 'field') return { ...prev, [targetKey]: { mode: 'field', source: current?.source || '' } };
      if (mode === 'concat') {
        return {
          ...prev,
          [targetKey]: { mode: 'concat', parts: current?.parts?.length ? current.parts : [{ type: 'text', value: '' }] },
        };
      }
      return { ...prev, [targetKey]: { mode: 'value', value: current?.value ?? '' } };
    });
  };

  const handleValueChange = (targetKey, raw) => {
    setMapping((prev) => ({ ...prev, [targetKey]: { mode: 'value', value: coerceValue(raw) } }));
  };

  const handleConcatPartsChange = (targetKey, parts) => {
    setMapping((prev) => ({ ...prev, [targetKey]: { mode: 'concat', parts } }));
  };

  const handleRemoveRow = (targetKey) => {
    setMapping((prev) => {
      const next = { ...prev };
      delete next[targetKey];
      return next;
    });
  };

  const handleAddSourceField = (path) => {
    if (!path || sourceFieldList.some((f) => f.path === path)) return false;
    setExtraSourceFields((prev) => [...prev, { path, label: path }]);
    return true;
  };

  const handleAddTargetField = (path) => {
    if (!path) return false;
    if (mapping[path]) {
      message.error(`'${path}' is already on this list`);
      return false;
    }
    setMapping((prev) => ({ ...prev, [path]: { mode: 'value', value: '' } }));
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveMapping(mapping);
      setMapping(saved || {});
      message.success('Field mapping saved');
    } catch (err) {
      message.error(err.message || 'Could not save field mapping');
    } finally {
      setSaving(false);
    }
  };

  return {
    mapping,
    catalog,
    loading,
    saving,
    error,
    sourceFieldList,
    sourceLabel,
    targetLabel,
    setFieldSource,
    handleModeChange,
    handleValueChange,
    handleConcatPartsChange,
    handleRemoveRow,
    handleAddSourceField,
    handleAddTargetField,
    handleSave,
  };
};

// A "concat" entry's parts editor -- literal text and/or Product fields
// (optionally rendered as an HTML table, for array-shaped sources like
// inventory-by-warehouse), joined in order into one string.
const CONCAT_FORMAT_OPTIONS = [
  { value: 'text', label: 'Plain text' },
  { value: 'html_table', label: 'HTML table' },
];

const ConcatPartsEditor = ({ parts, sourceFieldList, onChange }) => {
  const updatePart = (idx, patch) => {
    onChange(parts.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const removePart = (idx) => onChange(parts.filter((_, i) => i !== idx));
  const addPart = (type) =>
    onChange([...parts, type === 'text' ? { type: 'text', value: '' } : { type: 'field', source: '', format: 'text' }]);

  return (
    <div style={{ width: '100%', paddingLeft: 8, borderLeft: '2px solid rgba(0,0,0,0.08)' }}>
      {parts.map((part, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <Tag style={{ width: 46, textAlign: 'center', marginRight: 0 }}>{part.type === 'text' ? 'Text' : 'Field'}</Tag>
          {part.type === 'text' ? (
            <Input
              size="small"
              style={{ flex: 1 }}
              value={part.value || ''}
              onChange={(e) => updatePart(idx, { value: e.target.value })}
              placeholder="Literal text -- HTML tags allowed (e.g. <h4>Inventory</h4>)"
            />
          ) : (
            <>
              <Select
                size="small"
                style={{ width: 220 }}
                placeholder="Choose a Product field"
                value={part.source || undefined}
                onChange={(val) => updatePart(idx, { source: val })}
                options={sourceFieldList.map((f) => ({ value: f.path, label: f.label }))}
              />
              <Select
                size="small"
                style={{ width: 130 }}
                value={part.format || 'text'}
                onChange={(val) => updatePart(idx, { format: val })}
                options={CONCAT_FORMAT_OPTIONS}
              />
            </>
          )}
          <CloseCircleFilled onClick={() => removePart(idx)} style={{ cursor: 'pointer', color: '#999' }} />
        </div>
      ))}
      <Space size={8}>
        <Button size="small" onClick={() => addPart('text')}>
          + Text
        </Button>
        <Button size="small" onClick={() => addPart('field')}>
          + Field
        </Button>
      </Space>
    </div>
  );
};

const MODE_OPTIONS = [
  { label: 'Field', value: 'field' },
  { label: 'Value', value: 'value' },
  { label: 'Concat', value: 'concat' },
];

export const MappingCanvas = ({ fm }) => {
  const {
    mapping,
    catalog,
    loading,
    saving,
    error,
    sourceFieldList,
    sourceLabel,
    targetLabel,
    setFieldSource,
    handleModeChange,
    handleValueChange,
    handleConcatPartsChange,
    handleRemoveRow,
    handleAddSourceField,
    handleAddTargetField,
    handleSave,
  } = fm;

  const [lines, setLines] = useState([]);
  const [dragSourceKey, setDragSourceKey] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);
  const [addingSourceField, setAddingSourceField] = useState(false);
  const [newSourcePath, setNewSourcePath] = useState('');
  const [addingTargetField, setAddingTargetField] = useState(false);
  const [newTargetPath, setNewTargetPath] = useState('');

  const containerRef = useRef(null);
  const sourceRefs = useRef({});
  const targetRefs = useRef({});

  // Recompute the SVG connector lines from the current DOM position of
  // each node -- driven by ref callbacks + a resize listener rather than
  // fixed coordinates, so the diagram stays correct regardless of font
  // size, zoom, or window width.
  const recomputeLines = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const containerBox = container.getBoundingClientRect();
    const anchor = (el, side) => {
      const box = el.getBoundingClientRect();
      return {
        x: (side === 'right' ? box.right : box.left) - containerBox.left,
        y: box.top + box.height / 2 - containerBox.top,
      };
    };

    const next = [];
    const codeEl = targetRefs.current.code;
    const skuEl = sourceRefs.current.sku;
    if (codeEl && skuEl) {
      const from = anchor(skuEl, 'right');
      const to = anchor(codeEl, 'left');
      next.push({ key: 'code', locked: true, ...from, x2: to.x, y2: to.y });
    }
    Object.entries(mapping).forEach(([target, entry]) => {
      if (entry?.mode !== 'field' || !entry.source) return;
      const sourceEl = sourceRefs.current[entry.source];
      const targetEl = targetRefs.current[target];
      if (!sourceEl || !targetEl) return;
      const from = anchor(sourceEl, 'right');
      const to = anchor(targetEl, 'left');
      next.push({ key: target, locked: false, ...from, x2: to.x, y2: to.y });
    });
    setLines(next);
  }, [mapping]);

  useLayoutEffect(() => {
    recomputeLines();
  }, [recomputeLines]);

  useEffect(() => {
    window.addEventListener('resize', recomputeLines);
    return () => window.removeEventListener('resize', recomputeLines);
  }, [recomputeLines]);

  // getBoundingClientRect() on a node inside a `display: none` antd tab
  // pane returns an all-zero rect -- if `mapping` changes (shared with the
  // Constants tab) while this pane is hidden, recomputeLines above stores
  // zeroed-out lines with nothing to redraw them once the pane becomes
  // visible again. A ResizeObserver on the container catches exactly that
  // hidden-to-visible transition (a 0x0 -> real-size box change) and
  // recomputes then, same as any other resize.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(recomputeLines);
    observer.observe(container);
    return () => observer.disconnect();
  }, [recomputeLines]);

  const handleDragStart = (sourceKey) => (e) => {
    e.dataTransfer.setData('text/plain', sourceKey);
    e.dataTransfer.effectAllowed = 'copy';
    setDragSourceKey(sourceKey);
  };

  const handleDragEnd = () => {
    setDragSourceKey(null);
    setDragOverTarget(null);
  };

  const handleDragOverTarget = (targetKey) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverTarget(targetKey);
  };

  const handleDrop = (targetKey) => (e) => {
    e.preventDefault();
    const sourceKey = e.dataTransfer.getData('text/plain');
    // Dropping always creates/replaces a field mapping, regardless of
    // whatever mode the row was in before.
    if (sourceKey) setFieldSource(targetKey, sourceKey);
    setDragSourceKey(null);
    setDragOverTarget(null);
  };

  const submitAddTargetField = () => {
    const path = newTargetPath.trim();
    if (handleAddTargetField(path)) {
      setNewTargetPath('');
      setAddingTargetField(false);
    }
  };

  const submitAddSourceField = () => {
    const path = newSourcePath.trim();
    if (handleAddSourceField(path)) {
      setNewSourcePath('');
      setAddingSourceField(false);
    } else if (path) {
      message.error(`'${path}' is already on this list`);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Every field ServiceTitan receives when you publish a Catalog. Drag a Product field onto a
          ServiceTitan field to map it, switch a field to <Text italic>Value</Text> to set it to a fixed
          constant (e.g. Categories, Vendor ID), or to <Text italic>Concat</Text> to build it out of several
          fields and/or literal text -- e.g. a Description followed by an HTML table of inventory-by-
          warehouse. Use <Text italic>+ Add field</Text> on either side for anything not already listed --
          dot notation works for nested paths (e.g. <Text code>primaryVendor.vendorId</Text>). Click the{' '}
          <CloseCircleFilled style={{ color: '#999' }} /> on a ServiceTitan field to remove it so it isn&apos;t
          sent at all. Applies tenant-wide across all Catalogs.
        </Text>
      </div>
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      <div ref={containerRef} style={{ position: 'relative', display: 'flex', gap: 140, alignItems: 'flex-start' }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          {lines.map((line) => (
            <g key={line.key}>
              <path
                d={connectorPath(line.x, line.y, line.x2, line.y2)}
                stroke={line.locked ? LOCKED_LINE_COLOR : LINE_COLOR}
                strokeWidth={2}
                strokeDasharray={line.locked ? '4 4' : undefined}
                fill="none"
              />
              <circle cx={line.x} cy={line.y} r={4} fill={line.locked ? LOCKED_LINE_COLOR : LINE_COLOR} />
              <circle cx={line.x2} cy={line.y2} r={4} fill={line.locked ? LOCKED_LINE_COLOR : LINE_COLOR} />
            </g>
          ))}
        </svg>

        <div style={{ width: 240, flexShrink: 0 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Your Product Fields
          </Text>
          {sourceFieldList.map(({ path, label }) => (
            <div
              key={path}
              ref={(el) => {
                sourceRefs.current[path] = el;
              }}
              draggable
              onDragStart={handleDragStart(path)}
              onDragEnd={handleDragEnd}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                height: NODE_HEIGHT,
                marginBottom: NODE_GAP,
                padding: '0 10px',
                background: dragSourceKey === path ? '#e6f4ff' : '#fff',
                border: `1px solid ${dragSourceKey === path ? LINE_COLOR : 'rgba(0,0,0,0.15)'}`,
                borderRadius: 6,
                cursor: 'grab',
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
              }}
            >
              <HolderOutlined style={{ color: '#999' }} />
              <Text>{label}</Text>
            </div>
          ))}

          {addingSourceField ? (
            <Space.Compact style={{ width: '100%', marginTop: 4 }}>
              <AutoComplete
                autoFocus
                style={{ width: '100%' }}
                placeholder="e.g. attributes.color"
                value={newSourcePath}
                onChange={setNewSourcePath}
                onKeyDown={(e) => e.key === 'Enter' && submitAddSourceField()}
                options={sourceFieldList.map((f) => ({ value: f.path }))}
              />
              <Button onClick={submitAddSourceField}>Add</Button>
            </Space.Compact>
          ) : (
            <Button type="dashed" icon={<PlusOutlined />} block onClick={() => setAddingSourceField(true)}>
              Add Product Field
            </Button>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            ServiceTitan Fields
          </Text>

          <div
            ref={(el) => {
              targetRefs.current.code = el;
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: NODE_HEIGHT,
              marginBottom: NODE_GAP,
              padding: '0 11px',
              background: 'rgba(0,0,0,0.03)',
              borderRadius: 6,
            }}
          >
            <Text>Item Code</Text>
            <Text type="secondary" italic>
              Always SKU / Item Code
            </Text>
          </div>

          {Object.entries(mapping).map(([target, entry]) => {
            const isDragOver = dragOverTarget === target;
            const mode = entry?.mode === 'value' ? 'value' : entry?.mode === 'concat' ? 'concat' : 'field';
            return (
              <div
                key={target}
                data-testid={`mapping-row-${target}`}
                ref={(el) => {
                  targetRefs.current[target] = el;
                }}
                onDragOver={handleDragOverTarget(target)}
                onDragLeave={() => setDragOverTarget((current) => (current === target ? null : current))}
                onDrop={handleDrop(target)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  marginBottom: NODE_GAP,
                  padding: '4px 11px',
                  background: isDragOver ? '#e6f4ff' : '#fff',
                  border: `1px dashed ${isDragOver ? LINE_COLOR : 'rgba(0,0,0,0.15)'}`,
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    minHeight: NODE_HEIGHT,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <Text>{targetLabel(target)}</Text>
                    {targetLabel(target) !== target && (
                      <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                        ({target})
                      </Text>
                    )}
                  </div>
                  <Space size={8}>
                    <Segmented size="small" value={mode} onChange={(val) => handleModeChange(target, val)} options={MODE_OPTIONS} />
                    {mode === 'field' &&
                      (entry.source ? (
                        <Tag color="blue" style={{ marginRight: 0 }}>
                          {sourceLabel(entry.source)}
                        </Tag>
                      ) : (
                        <Text type="secondary" italic>
                          Drop a field here
                        </Text>
                      ))}
                    {mode === 'value' && (
                      <Input
                        size="small"
                        style={{ width: 160 }}
                        value={displayValue(entry.value)}
                        onChange={(e) => handleValueChange(target, e.target.value)}
                        placeholder="Fixed value"
                      />
                    )}
                    <CloseCircleFilled onClick={() => handleRemoveRow(target)} style={{ cursor: 'pointer', color: '#999' }} />
                  </Space>
                </div>
                {mode === 'concat' && (
                  <ConcatPartsEditor
                    parts={entry.parts || []}
                    sourceFieldList={sourceFieldList}
                    onChange={(parts) => handleConcatPartsChange(target, parts)}
                  />
                )}
              </div>
            );
          })}

          {addingTargetField ? (
            <Space.Compact style={{ width: '100%', marginTop: 4 }}>
              <AutoComplete
                autoFocus
                style={{ width: '100%' }}
                placeholder="e.g. primaryVendor.vendorId"
                value={newTargetPath}
                onChange={setNewTargetPath}
                onKeyDown={(e) => e.key === 'Enter' && submitAddTargetField()}
                options={(catalog.target_fields || [])
                  .filter((f) => !mapping[f.path])
                  .map((f) => ({ value: f.path, label: `${f.label} (${f.path})` }))}
              />
              <Button onClick={submitAddTargetField}>Add</Button>
            </Space.Compact>
          ) : (
            <Button type="dashed" icon={<PlusOutlined />} block onClick={() => setAddingTargetField(true)}>
              Add ServiceTitan Field
            </Button>
          )}

          <Button type="primary" onClick={handleSave} loading={saving} disabled={loading} style={{ marginTop: 16 }}>
            Save Mapping
          </Button>
        </div>
      </div>
    </div>
  );
};

// Friendlier labels than the raw target path for the fixed set of
// constant rows -- falls back to the field catalog's label (or the path
// itself) for anything not covered here.
const CONSTANT_FALLBACK_LABELS = {
  categories: 'Categories',
  active: 'Active',
  taxable: 'Taxable',
  account: 'Account',
  costOfSaleAccount: 'Cost of Sale Account',
  'primaryVendor.vendorId': 'Vendor ID',
};

export const ConstantsPanel = ({ fm }) => {
  const {
    mapping,
    loading,
    saving,
    error,
    sourceFieldList,
    targetLabel,
    setFieldSource,
    handleModeChange,
    handleValueChange,
    handleConcatPartsChange,
    handleRemoveRow,
    handleSave,
  } = fm;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          Fixed ServiceTitan values that don&apos;t vary per Catalog item. Switch a field to{' '}
          <Text italic>Field</Text> to map it from a Product field instead (or do that with drag-and-drop on
          the Mapping tab) -- both tabs edit the same mapping, so a change here shows up there too.
        </Text>
      </div>
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

      {CONSTANT_TARGET_PATHS.map((target) => {
        const entry = mapping[target] || { mode: 'value', value: '' };
        const mode = entry.mode === 'field' ? 'field' : entry.mode === 'concat' ? 'concat' : 'value';
        const label = CONSTANT_FALLBACK_LABELS[target] || targetLabel(target);
        return (
          <div
            key={target}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: NODE_GAP,
              padding: '4px 11px',
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: NODE_HEIGHT }}>
              <div style={{ minWidth: 0 }}>
                <Text>{label}</Text>
                {label !== target && (
                  <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                    ({target})
                  </Text>
                )}
              </div>
              <Space size={8}>
                <Segmented size="small" value={mode} onChange={(val) => handleModeChange(target, val)} options={MODE_OPTIONS} />
                {mode === 'field' && (
                  <Select
                    size="small"
                    style={{ width: 200 }}
                    placeholder="Choose a Product field"
                    value={entry.source || undefined}
                    onChange={(val) => setFieldSource(target, val)}
                    options={sourceFieldList.map((f) => ({ value: f.path, label: f.label }))}
                  />
                )}
                {mode === 'value' && (
                  <Input
                    size="small"
                    style={{ width: 200 }}
                    value={displayValue(entry.value)}
                    onChange={(e) => handleValueChange(target, e.target.value)}
                    placeholder="Fixed value"
                  />
                )}
                <CloseCircleFilled onClick={() => handleRemoveRow(target)} style={{ cursor: 'pointer', color: '#999' }} />
              </Space>
            </div>
            {mode === 'concat' && (
              <ConcatPartsEditor
                parts={entry.parts || []}
                sourceFieldList={sourceFieldList}
                onChange={(parts) => handleConcatPartsChange(target, parts)}
              />
            )}
          </div>
        );
      })}

      <Button type="primary" onClick={handleSave} loading={saving} disabled={loading} style={{ marginTop: 16 }}>
        Save Constants
      </Button>
    </div>
  );
};

const ServiceTitanTabs = () => {
  const fm = useFieldMapping();
  return (
    <Tabs
      items={[
        { key: 'mapping', label: 'Mapping', children: <MappingCanvas fm={fm} /> },
        { key: 'constants', label: 'Constants', children: <ConstantsPanel fm={fm} /> },
      ]}
    />
  );
};

const PipesPanel = () => (
  <div>
    <Text type="secondary">
      Pipes are the FSM platform integrations Catalogs can publish into. ServiceTitan is the only pipe in
      this vertical slice.
    </Text>
    <Collapse
      style={{ marginTop: 16 }}
      items={[
        {
          key: 'service_titan',
          label: 'ServiceTitan',
          children: <ServiceTitanTabs />,
        },
      ]}
    />
  </div>
);

export default PipesPanel;
